const DURATION = 5 * 60 * 1000;
const STORAGE_KEY = 'furlabs:cart-reminder:v1';
let deadline = null;

function resetReminder() {
  deadline = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Keep working in memory when browser storage is unavailable.
  }
}

function getDeadline() {
  if (deadline !== null) return deadline;

  try {
    const saved = Number(sessionStorage.getItem(STORAGE_KEY));
    if (Number.isSafeInteger(saved) && saved > 0 && saved <= Date.now() + DURATION) {
      deadline = saved;
    }
  } catch {
    // A blocked storage API must never interrupt the cart.
  }

  if (deadline === null) {
    deadline = Date.now() + DURATION;
    try {
      sessionStorage.setItem(STORAGE_KEY, String(deadline));
    } catch {
      // All instances still share the same in-memory deadline.
    }
  }
  return deadline;
}

if (document.getElementById('furlabs-cart-reminder-script')?.dataset.cartEmpty === 'true') {
  resetReminder();
}

// Reset only after a successful empty-cart response, never an optimistic removal.
document.addEventListener('shopify:cart:lines-update', (event) => {
  event.promise?.then((result) => {
    if (result?.cart?.totalQuantity === 0 && !result.detail?.didError && !result.userErrors?.length) {
      resetReminder();
    }
  }).catch(() => {});
});

class FurlabsCartReminder extends HTMLElement {
  static observedAttributes = ['hidden'];

  #interval;
  #drawer;
  #ends;
  #announced = false;

  connectedCallback() {
    this.#drawer = this.closest('theme-drawer');
    this.#drawer?.addEventListener('theme-drawer:open', this.#resume);
    this.#drawer?.addEventListener('theme-drawer:close', this.#stop);
    document.addEventListener('visibilitychange', this.#resume);
    window.addEventListener('pageshow', this.#resume);
    this.#resume();
  }

  disconnectedCallback() {
    this.#stop();
    this.#drawer?.removeEventListener('theme-drawer:open', this.#resume);
    this.#drawer?.removeEventListener('theme-drawer:close', this.#stop);
    document.removeEventListener('visibilitychange', this.#resume);
    window.removeEventListener('pageshow', this.#resume);
  }

  // Cart section updates restore the server-rendered hidden attribute. Resume
  // after that morph completes, including when an expired timer has stopped.
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'hidden' && newValue !== null && this.isConnected) {
      queueMicrotask(() => {
        if (this.isConnected) this.#resume();
      });
    }
  }

  #stop = () => {
    clearInterval(this.#interval);
  };

  #resume = () => {
    this.#stop();
    if (document.hidden || (this.#drawer && !this.#drawer.hasAttribute('open'))) return;

    this.#ends = getDeadline();
    this.#tick();
    if (this.#ends > Date.now()) {
      this.#interval = setInterval(() => this.#tick(), 1000);
    }
  };

  #tick() {
    const seconds = Math.max(0, Math.ceil((this.#ends - Date.now()) / 1000));
    const expired = seconds === 0;
    const minutesEl = this.querySelector('[data-reminder-minutes]');
    const secondsEl = this.querySelector('[data-reminder-seconds]');
    const activeEl = this.querySelector('[data-reminder-active]');
    const expiredEl = this.querySelector('[data-reminder-expired]');
    if (!minutesEl || !secondsEl || !activeEl || !expiredEl) return;

    minutesEl.textContent = String(Math.floor(seconds / 60)).padStart(2, '0');
    secondsEl.textContent = String(seconds % 60).padStart(2, '0');
    this.querySelector('[data-reminder-progress]')?.style.setProperty('--remaining', String(seconds / 300));
    activeEl.hidden = expired;
    expiredEl.hidden = !expired;
    this.toggleAttribute('data-expired', expired);
    this.hidden = false;

    if (expired) {
      this.#stop();
      if (!this.#announced) {
        const status = this.querySelector('[data-reminder-status]');
        if (status) status.textContent = expiredEl.textContent.trim();
        this.#announced = true;
      }
    }
  }
}

if (!customElements.get('furlabs-cart-reminder')) {
  customElements.define('furlabs-cart-reminder', FurlabsCartReminder);
}
